import { useQuery } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

import { api } from '../../lib/api'
import { usePeople } from '../../lib/time'
import { canAny, type User as AppUser } from '../../lib/types'
import type { Covering } from './covering'
import { offeredScopes, resolveRequestedScope } from './scopes'

export type Scope = 'own' | 'team' | 'all' | 'awaiting' | 'admin'

interface SuperviseesResponse {
  owned: string[]
  all: string[]
  covering?: Covering[]
}

function useSuperviseeIds() {
  return useQuery({
    queryKey: ['time', 'supervisees'] as const,
    queryFn: async () => (await api.get<SuperviseesResponse>('/api/time/supervisees')).data,
    staleTime: 5 * 60_000,
  })
}

export function useRoster() {
  const { data, isLoading, error, refetch, isFetching } = usePeople()
  const { data: resolved, isPending: superviseesPending } = useSuperviseeIds()
  const people = useMemo(() => data?.people ?? [], [data])
  const me = useMemo(() => people.find((p) => p.is_self), [people])
  const supervisees = useMemo(() => {
    if (!me) return []
    if (!resolved) return people.filter((p) => p.supervisor_id === me.id)
    const mine = new Set(resolved.all)
    return people.filter((p) => mine.has(p.id))
  }, [people, me, resolved])
  return {
    people,
    me,
    supervisees,
    covering: resolved?.covering,
    /** Settled once the server's supervisee list answered or failed; before that `supervisees` is only the reporting line. */
    superviseesKnown: !superviseesPending,
    isLoading,
    loadError: error,
    retry: refetch,
    retrying: isFetching,
    total: data?.total ?? people.length,
    truncated: data?.truncated ?? false,
  }
}

/**
 * Falls back to 'own' whenever the wider scopes are not available, so a URL or a stale state can
 * never leave someone on a tab they cannot see.
 */
export function useScope(user: AppUser | undefined, adminAbility: string | string[]) {
  const abilityKey = Array.isArray(adminAbility) ? adminAbility.join(',') : adminAbility
  const { people, me, supervisees, covering, superviseesKnown, isLoading, loadError, retry, retrying } = useRoster()
  const isAdmin = canAny(user, abilityKey.split(','))

  const scopes = useMemo(() => offeredScopes(isAdmin), [isAdmin])

  // Superset of `scopes` used only to resolve `?scope=` -- keeps `team` and `all` working for anyone
  // still linking to them, without offering a tab.
  const recognized = useMemo(() => {
    const list = [...scopes]
    if (supervisees.length > 0) list.push('team')
    if (isAdmin) list.push('all')
    return list
  }, [scopes, supervisees.length, isAdmin])

  const [searchParams, setSearchParams] = useSearchParams()
  const requested = resolveRequestedScope(searchParams.get('scope'), isAdmin) as Scope | null
  const active: Scope = requested && recognized.includes(requested) ? requested : 'own'

  const setScope = useCallback(
    (next: Scope) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev)
          if (next === 'own') params.delete('scope')
          else params.set('scope', next)
          return params
        },
        { replace: true },
      )
    },
    [setSearchParams],
  )

  return { scope: active, setScope, scopes, people, me, supervisees, covering, superviseesKnown, isLoading, loadError, retry, retrying }
}
